import React, { useEffect, useState, useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaArrowLeft, FaUserShield, FaEnvelope, FaCalendarAlt,
  FaClock, FaShieldAlt, FaCheckCircle, FaTimesCircle,
  FaUserSlash, FaEdit,
} from 'react-icons/fa';
import AppContext from '../../../context/AppContext';
import '../../../assets/css/admin/adminUsersManagement/admindetailscreen.css';

const getAvatarColor = (id = '', name = '') => {
  const colors = [
    '#2C5F7C', '#4A9E8E', '#FFB800', '#DC3545', '#17A2B8',
    '#6C757D', '#28A745', '#E83E8C', '#FD7E14', '#20C997',
  ];
  const str = (id + name).toString();
  const hash = str.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  return colors[Math.abs(hash) % colors.length];
};

const formatDate = (d) => {
  if (!d) return 'Not available';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const AdminDetailScreen = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { baseUrl,authToken } = useContext(AppContext);

  const [admin, setAdmin] = useState(state?.user || null);
  const [loading, setLoading] = useState(!state?.user);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: '', type: 'success' });

  useEffect(() => {
    if (!admin && id) {
      setLoading(true);
      axios.get(`${baseUrl}/admin/${id}`)
        .then((res) => {
          if (res.data.success) setAdmin(res.data.admin);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id, admin, baseUrl]);

  const showToast = (msg, type = 'success') => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast({ show: false, msg: '', type: 'success' }), 3000);
  };

  const handleToggleStatus = () => {
    if (!admin) return;
    const newStatus = admin.admaccount_status === 'active' ? 'inactive' : 'active';
    setActionLoading(true);

    const fd = new FormData();
    fd.append('id', admin.id);
    fd.append('status', newStatus);

    axios.post(`${baseUrl}/admin/update-status`, fd, {
      headers: {  headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
        }},
    })
      .then((res) => {
        if (res.data.success) {
          setAdmin((prev) => ({ ...prev, admaccount_status: newStatus }));
          showToast(`Admin ${newStatus} successfully.`);
        }
      })
      .catch(() => showToast('Action failed', 'error'))
      .finally(() => setActionLoading(false));
  };

  if (loading) {
    return (
      <div className="ads-loading">
        <div className="ads-spinner" />
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="ads-not-found">
        <h3>Admin not found</h3>
        <button className="ads-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Go Back
        </button>
      </div>
    );
  }

  const isActive = admin.admaccount_status === 'active';
  const avatarBg = getAvatarColor(admin.id, admin.admusername);

  return (
    <div className="ads-container">

      {toast.show && (
        <div className={`ads-toast ads-toast-${toast.type}`}>
          {toast.msg}
        </div>
      )}

      <div className="ads-topbar">
        <button className="ads-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h1 className="ads-page-title">Admin Details</h1>
      </div>

      <div className="ads-hero">
        <div className="ads-avatar" style={{ background: avatarBg }}>
          {admin.admusername?.charAt(0).toUpperCase() || 'A'}
        </div>
        <div className="ads-hero-info">
          <h2 className="ads-name">
            {admin.admusername}
            {isActive && <FaCheckCircle className="ads-verified-icon" />}
          </h2>
          <div className="ads-badges">
            <span className={`ads-status-badge ${isActive ? 'ads-active' : 'ads-inactive'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
            <span className="ads-role-badge">
              <FaUserShield /> Administrator
            </span>
          </div>
          <p className="ads-id">User ID: #{admin.id}</p>
        </div>

        <div className="ads-hero-actions">
          <button
            className={`ads-action-btn ${isActive ? 'ads-btn-danger' : 'ads-btn-success'}`}
            onClick={handleToggleStatus}
            disabled={actionLoading}
          >
            {isActive ? <><FaUserSlash /> Deactivate</> : <><FaCheckCircle /> Activate</>}
          </button>
        </div>
      </div>

      <div className="ads-grid">

        <div className="ads-card">
          <h3 className="ads-card-title">
            <FaEnvelope className="ads-card-icon" /> Contact Information
          </h3>
          <div className="ads-field">
            <label>Email Address</label>
            <p>{admin.admemail || '—'}</p>
          </div>
          <div className="ads-field">
            <label>Phone Number</label>
            <p>{admin.admphone || 'Not provided'}</p>
          </div>
          <div className="ads-field">
            <label>Location</label>
            <p>{admin.admlocation || 'Not specified'}</p>
          </div>
        </div>

        <div className="ads-card">
          <h3 className="ads-card-title">
            <FaShieldAlt className="ads-card-icon" /> Account Information
          </h3>
          <div className="ads-field">
            <label>Account Status</label>
            <p className={isActive ? 'ads-text-success' : 'ads-text-danger'}>
              {isActive ? 'Active' : 'Inactive'}
            </p>
          </div>
          <div className="ads-field">
            <label>Joined Date</label>
            <p>{formatDate(admin.created_at)}</p>
          </div>
          <div className="ads-field">
            <label>Last Updated</label>
            <p>{formatDate(admin.updated_at)}</p>
          </div>
        </div>

        <div className="ads-card ads-card-full">
          <h3 className="ads-card-title">
            <FaShieldAlt className="ads-card-icon" /> Permissions & Access
          </h3>
          <div className="ads-permissions-grid">
            {[
              { label: 'User Management',    allowed: true },
              { label: 'Content Management', allowed: true },
              { label: 'Analytics Access',   allowed: true },
              { label: 'System Settings',    allowed: true },
              { label: 'Billing Management', allowed: false },
              { label: 'API Access',         allowed: false },
            ].map((perm) => (
              <div key={perm.label} className="ads-perm-item">
                {perm.allowed
                  ? <FaCheckCircle className="ads-perm-icon ads-perm-allowed" />
                  : <FaTimesCircle className="ads-perm-icon ads-perm-denied" />
                }
                <span>{perm.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="ads-card">
          <h3 className="ads-card-title">
            <FaClock className="ads-card-icon" /> Activity Summary
          </h3>
          <div className="ads-activity-row">
            <div className="ads-activity-stat">
              <span className="ads-act-value">156</span>
              <span className="ads-act-label">Logins</span>
            </div>
            <div className="ads-activity-stat">
              <span className="ads-act-value">23</span>
              <span className="ads-act-label">Actions</span>
            </div>
            <div className="ads-activity-stat">
              <span className="ads-act-value">7</span>
              <span className="ads-act-label">Reports</span>
            </div>
          </div>
        </div>

        <div className="ads-card">
          <h3 className="ads-card-title">
            <FaCalendarAlt className="ads-card-icon" /> Additional Info
          </h3>
          <div className="ads-field">
            <label>User Type</label>
            <p>System Administrator</p>
          </div>
          <div className="ads-field">
            <label>Department</label>
            <p>IT Administration</p>
          </div>
          <div className="ads-field">
            <label>Reports To</label>
            <p>Super Admin</p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminDetailScreen;